import { isIOS, checkWebSocketSupport } from '../utils/polyfills';

// 後端驗證不過（沒帶 token、token 過期或已被登出）時的關閉碼，同 server/websockets/wsAccess.js。
// 收到就不重連：同一個 token 再連只會一直被拒；換新 token 由 GlobalContext 重建連線
export const WS_UNAUTHORIZED_CODE = 4401;

class WebSocketService {
    constructor() {
        this.connections = new Map(); // 儲存多個WebSocket連接
        this.isIOS = isIOS();
        this.maxReconnectAttempts = 10;
    }

    connect(url, onDataUpdate, key = 'default') {
        // 檢查 WebSocket 支持
        if (!checkWebSocketSupport()) {
            console.error('[WebSocketService] WebSocket not supported');
            return;
        }

        // 如果已存在該key的連接，先斷開
        if (this.connections.has(key)) {
            this.disconnect(key);
        }

        const connectionInfo = {
            ws: null,
            reconnectInterval: null,
            mongoReconnectDelay: 1000,
            reconnectAttempts: 0,
            url,
            onDataUpdate,
            key
        };

        this.connections.set(key, connectionInfo);
        
        // 添加延遲，避免同時創建多個連接
        const delay = this.connections.size * 100; // 每個連接延遲100ms
        setTimeout(() => {
            // 延遲期間已被 disconnect（例如 token 換了、effect 重跑）就別再連，否則會留下沒人管的連線
            if (this.connections.get(key) !== connectionInfo) return;
            this._createConnection(connectionInfo);
        }, delay);
    }

    _createConnection(connectionInfo) {
        try {
            // 確保舊連接完全清理
            if (connectionInfo.ws) {
                connectionInfo.ws.close();
                connectionInfo.ws = null;
            }
            
            // console.log(`[WebSocketService] Connecting to: ${connectionInfo.url} (${connectionInfo.key})`);
            connectionInfo.ws = new WebSocket(connectionInfo.url);

            connectionInfo.ws.onopen = () => {
                // console.log(`[WebSocketService] WebSocket connected successfully (${connectionInfo.key})`);
                connectionInfo.reconnectAttempts = 0;

                if (connectionInfo.reconnectInterval) {
                    clearTimeout(connectionInfo.reconnectInterval);
                    connectionInfo.reconnectInterval = null;
                }

                this._resetReconnectDelay(connectionInfo);
            };

            connectionInfo.ws.onmessage = (event) => {
                try {
                    if (this.isIOS) {
                        const data = JSON.parse(event.data);
                        if (connectionInfo.onDataUpdate) {
                            connectionInfo.onDataUpdate(data);
                        }
                    } else {
                        requestIdleCallback(() => {
                            try {
                                const data = JSON.parse(event.data);
                                if (connectionInfo.onDataUpdate) {
                                    connectionInfo.onDataUpdate(data);
                                }
                            } catch (parseError) {
                                console.error(`[WebSocketService] Error parsing message (${connectionInfo.key}):`, parseError);
                            }
                        });
                    }
                } catch (error) {
                    console.error(`[WebSocketService] Error handling message (${connectionInfo.key}):`, error);
                }
            };

            connectionInfo.ws.onerror = (error) => {
                console.error(`[WebSocketService] WebSocket error (${connectionInfo.key}):`, error);
            };

            connectionInfo.ws.onclose = (event) => {
                console.warn(`[WebSocketService] WebSocket closed (${connectionInfo.key}): Code=${event.code}, Reason=${event.reason || 'Unknown'}`);

                if (event.code === WS_UNAUTHORIZED_CODE) {
                    return;
                }

                if (event.code !== 1000 && connectionInfo.reconnectAttempts < this.maxReconnectAttempts) {
                    this._reconnect(connectionInfo);
                } else if (connectionInfo.reconnectAttempts >= this.maxReconnectAttempts) {
                    console.error(`[WebSocketService] Max reconnection attempts reached (${connectionInfo.key})`);
                }
            };
        } catch (error) {
            console.error(`[WebSocketService] Error creating WebSocket (${connectionInfo.key}):`, error);
            this._reconnect(connectionInfo);
        }
    }

    disconnect(key = 'default') {
        const connectionInfo = this.connections.get(key);
        if (connectionInfo) {
            if (connectionInfo.ws) {
                console.log(`[WebSocketService] Disconnecting WebSocket (${key})...`);
                connectionInfo.ws.close(1000, 'Manual disconnect');
                connectionInfo.ws = null;
            }

            if (connectionInfo.reconnectInterval) {
                clearTimeout(connectionInfo.reconnectInterval);
                connectionInfo.reconnectInterval = null;
            }

            this.connections.delete(key);
        }
    }

    disconnectAll() {
        console.log('[WebSocketService] Disconnecting all WebSocket connections...');
        for (const [key] of this.connections) {
            this.disconnect(key);
        }
    }

    send(data, key = 'default') {
        const connectionInfo = this.connections.get(key);
        if (connectionInfo && connectionInfo.ws && connectionInfo.ws.readyState === WebSocket.OPEN) {
            try {
                connectionInfo.ws.send(data);
            } catch (error) {
                console.error(`[WebSocketService] Error sending data (${key}):`, error);
            }
        } else {
            console.warn(`[WebSocketService] WebSocket not ready (${key}). State:`,
                connectionInfo && connectionInfo.ws ? connectionInfo.ws.readyState : 'null');
        }
    }

    _reconnect(connectionInfo) {
        if (connectionInfo.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error(`[WebSocketService] Max reconnection attempts reached (${connectionInfo.key})`);
            return;
        }

        connectionInfo.reconnectAttempts++;
        const delay = Math.min(connectionInfo.mongoReconnectDelay * connectionInfo.reconnectAttempts, 30000);

        console.log(`[WebSocketService] Reconnecting in ${delay / 1000}s (attempt ${connectionInfo.reconnectAttempts}/${this.maxReconnectAttempts}) (${connectionInfo.key})`);

        connectionInfo.reconnectInterval = setTimeout(() => {
            this._createConnection(connectionInfo);
        }, delay);
    }

    _resetReconnectDelay(connectionInfo) {
        connectionInfo.mongoReconnectDelay = 5000;
        connectionInfo.reconnectAttempts = 0;
    }
}

export const websocketService = new WebSocketService();

